import { useState, useEffect, useCallback } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'
import { CriticalReminderBanner } from '../reminders/CriticalReminderBanner'
import { useDispatch } from 'react-redux'
import { Capacitor } from '@capacitor/core'
import { MobileTopBar } from './mobile/MobileTopBar'
import { MobileBottomNav } from './mobile/MobileBottomNav'
import { MobileDrawer } from './mobile/MobileDrawer'
import { MobileOverflowSheet } from './mobile/MobileOverflowSheet'
import { useMotivatorNotifications } from '../../hooks/useMotivatorNotifications'
import { logout } from '../../store/slices/authSlice'
import { AuthService } from '../../services/authService'
import { FloatingHortonButton } from '../ai/FloatingHortonButton'

export function MobileLayout() {
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [overflowOpen, setOverflowOpen] = useState(false)
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { unreadCount } = useMotivatorNotifications(60000)

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return
    let mounted = true
    ;(async () => {
      try {
        const { SplashScreen } = await import('@capacitor/splash-screen')
        if (mounted) await SplashScreen.hide()
      } catch { /* splash already hidden or plugin missing */ }
    })()
    return () => { mounted = false }
  }, [])

  const handleLogout = useCallback(async () => {
    try {
      const auth = new AuthService()
      await auth.logout()
    } catch { /* continue even on failure */ }
    dispatch(logout())
    navigate('/login')
  }, [dispatch, navigate])

  return (
    <div className="flex flex-col h-dvh bg-surface">
      {/* Skip link: first tab stop, so a keyboard reader can get past the top
          bar (2026-08-29). */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-[100] focus:inline-flex focus:min-h-touch focus:items-center focus:rounded-lg focus:bg-surface focus:px-4 focus:text-app-body focus:font-semibold focus:text-primary focus:shadow-lg focus:outline focus:outline-2 focus:outline-brand"
      >
        Skip to main content
      </a>

      <MobileTopBar
        onMenuClick={() => setDrawerOpen(true)}
        onOverflowClick={() => setOverflowOpen(true)}
        unreadCount={unreadCount}
        /* 2026-08-29: the bell opened the overflow menu, which has nothing to
           do with notifications. It goes to the notifications history now. */
        onNotifClick={() => navigate('/notifications')}
      />

      <CriticalReminderBanner />
      <main id="main-content" tabIndex={-1} className="flex-1 overflow-auto p-3">
        <Outlet />
      </main>

      <MobileBottomNav onMoreClick={() => setDrawerOpen(true)} />
      <FloatingHortonButton />

      <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} onLogout={handleLogout} />
      <MobileOverflowSheet open={overflowOpen} onClose={() => setOverflowOpen(false)} onLogout={handleLogout} />
    </div>
  )
}
